/**
 * The line a Kalshi total ticker's whole-number strike stands for
 * (`KXNHLTOTAL-26SEP30NYITOR-6` → 5.5), for ledger exports that only have the
 * ticker. Dashboard fallbacks share this helper; live `floorStrike` and
 * subtitle remain higher-priority sources where available.
 *
 * The rule depends on when the event was listed, not on the sport. Every
 * total series listed for the 2026-27 seasons means "Over N − 0.5": checked
 * against Kalshi's public API on 2026-10-01 for KXNHLTOTAL, KXNFLTOTAL,
 * KXNCAAFTOTAL, KXMLBTOTAL and KXWNBATOTAL. MLB and WNBA have always used it.
 *
 * Before those seasons the NBA, NFL, NHL and NCAA series meant "Over N + 0.5",
 * and their settled markets still say so (`floor_strike`): KXNFLTOTAL through
 * 26FEB08, KXNBATOTAL and KXNHLTOTAL through the June 2026 finals,
 * KXNCAAFTOTAL for the 2025 season, KXNCAAMBTOTAL / KXNCAAWBTOTAL for March
 * 2026. The last old-rule event is 2026-06-14 and the first new-rule one is
 * 2026-08-06, so one cutoff splits every series. No new-season KXNBATOTAL or
 * NCAA basketball total was listed yet on 2026-10-01; they are assumed to
 * have switched with the rest.
 */

/** Series roots that meant "Over N + 0.5" before the 2026-27 seasons. */
const LEGACY_PLUS_HALF_SERIES = ['KXNBA', 'KXNFL', 'KXNHL', 'KXNCAA'] as const;
/** Events dated before this keep the legacy rule. */
const LEGACY_CUTOFF_DATE = '2026-07-01';

const MONTHS: Record<string, string> = {
  JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
  JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12',
};

export function kalshiTotalLineFromStrike(ticker: string, strike: number): number {
  return isLegacyPlusHalfTotal(ticker) ? strike + 0.5 : strike - 0.5;
}

function isLegacyPlusHalfTotal(ticker: string): boolean {
  const [series = '', event = ''] = ticker.trim().toUpperCase().split('-');
  if (!LEGACY_PLUS_HALF_SERIES.some((root) => series.startsWith(root))) return false;
  const date = eventDate(event);
  return date != null && date < LEGACY_CUTOFF_DATE;
}

/** `26SEP30NYITOR` → '2026-09-30'. */
function eventDate(event: string): string | null {
  const m = event.match(/^(\d{2})([A-Z]{3})(\d{2})/);
  const month = m ? MONTHS[m[2]] : undefined;
  return m && month ? `20${m[1]}-${month}-${m[3]}` : null;
}
